import Box from '@mui/material/Box';
import {UserLink} from '@wandb/weave/components/UserLink';
import {useObjectViewEvent} from '@wandb/weave/integrations/analytics/useViewEvents';
import {ObjectRef} from '@wandb/weave/react';
import React, {useMemo} from 'react';

import {useObjectStorageSizeCalculation} from '../../../../../../common/hooks/useStorageSizeCalculation';
import {maybePluralizeWord} from '../../../../../../core/util/string';
import {Icon, IconName} from '../../../../../Icon';
import {LoadingDots} from '../../../../../LoadingDots';
import {Tailwind} from '../../../../../Tailwind';
import {Timestamp} from '../../../../../Timestamp';
import {Tooltip} from '../../../../../Tooltip';
import {DatasetEditProvider} from '../../datasets/DatasetEditorContext';
import {DatasetVersionPage} from '../../datasets/DatasetVersionPage';
import {
  callQueryFieldForScorerOutput,
  callQueryFieldForScorerVersion,
} from '../../feedback/StructuredFeedback/runnableFeedbackTypes';
import {NotFoundPanel} from '../../NotFoundPanel';
import {PromptVersionPage} from '../../prompts/PromptVersionPage';
import {SmallRef} from '../../smallRef/SmallRef';
import {CustomWeaveTypeProjectContext} from '../../typeViews/CustomWeaveTypeDispatcher';
import {WeaveCHTableSourceRefContext} from '../CallPage/DataTableView';
import {ObjectViewerSection} from '../CallPage/ObjectViewerSection';
import {WFHighLevelCallFilter} from '../CallsPage/callsTableFilter';
import {useShowDeleteButton} from '../common/DeleteModal';
import {
  CallLink,
  CallsLink,
  ObjectVersionsLink,
  objectVersionText,
} from '../common/Links';
import {CenteredAnimatedLoader} from '../common/Loader';
import {
  ScrollableTabContent,
  SimpleKeyValueTable,
  SimplePageLayoutWithHeader,
} from '../common/SimplePageLayout';
import {StorageSizeSection} from '../common/StorageSizeSection';
import {EvaluationLeaderboardTab} from '../LeaderboardTab';
import {MonitorPage} from '../MonitorsPage/MonitorPage';
import {TabUsePrompt} from '../OpsPage/Tabs/TabUsePrompt';
import {KNOWN_BASE_OBJECT_CLASSES} from '../wfReactInterface/constants';
import {useWFHooks} from '../wfReactInterface/context';
import {useCalls, useCallsStats} from '../wfReactInterface/tsDataModelHooks';
import {
  isObjDeleteError,
  objectVersionKeyToRefUri,
  refUriToOpVersionKey,
} from '../wfReactInterface/utilities';
import {
  CallSchema,
  KnownBaseObjectClassType,
  ObjectVersionSchema,
  WeaveObjectVersionKey,
} from '../wfReactInterface/wfDataModelHooksInterface';
import {DeleteObjectButtonWithModal} from './ObjectDeleteButtons';
import {TabUseAnnotationSpec} from './Tabs/TabUseAnnotationSpec';
import {TabUseModel} from './Tabs/TabUseModel';
import {TabUseObject} from './Tabs/TabUseObject';
import {TabUseSavedView} from './Tabs/TabUseSavedView';

type ObjectIconProps = {
  baseObjectClass: KnownBaseObjectClassType;
};
const OBJECT_ICONS: Record<KnownBaseObjectClassType, IconName> = {
  Prompt: 'forum-chat-bubble',
  Model: 'model',
  Dataset: 'table',
  Evaluation: 'baseline-alt',
  EvaluationResults: 'baseline-alt',
  Leaderboard: 'benchmark-square',
  Scorer: 'type-number-alt',
  ActionSpec: 'rocket-launch',
  AnnotationSpec: 'forum-chat-bubble',
  SavedView: 'view-glasses',
  Provider: 'model',
  ProviderModel: 'model',
  Monitor: 'job-automation',
  LLMStructuredCompletionModel: 'model',
  Op: 'code-alt',
};
export const ObjectIcon = ({baseObjectClass}: ObjectIconProps) => {
  if (baseObjectClass in OBJECT_ICONS) {
    const iconName = OBJECT_ICONS[baseObjectClass];
    return (
      <Tooltip
        trigger={
          <div className="flex h-22 w-22 items-center justify-center rounded-full bg-moon-300/[0.48] text-moon-600">
            <Icon width={14} height={14} name={iconName} />
          </div>
        }
        content={baseObjectClass}
      />
    );
  }
  return null;
};

export const ObjectVersionPage: React.FC<{
  entity: string;
  project: string;
  objectName: string;
  version: string;
  filePath: string;
  refExtra?: string;
}> = props => {
  const {useObjectVersion} = useWFHooks();

  const objectVersion = useObjectVersion({
    // Blindly assume this is weave object?
    key: {
      scheme: 'weave' as WeaveObjectVersionKey['scheme'],
      weaveKind: 'object' as WeaveObjectVersionKey['weaveKind'],
      entity: props.entity,
      project: props.project,
      objectId: props.objectName,
      versionHash: props.version,
      path: props.filePath,
      refExtra: props.refExtra,
    },
  });
  if (isObjDeleteError(objectVersion.error)) {
    const deletedAtMessage = objectVersion.error?.message ?? 'Object deleted';
    return <NotFoundPanel title={deletedAtMessage} />;
  } else if (objectVersion.loading) {
    return <CenteredAnimatedLoader />;
  } else if (objectVersion.result == null) {
    return <NotFoundPanel title="Object not found" />;
  }
  if (objectVersion.result.baseObjectClass === 'Monitor') {
    return <MonitorPage {...props} objectVersion={objectVersion.result} />;
  }

  return (
    <ObjectVersionPageInner {...props} objectVersion={objectVersion.result} />
  );
};
const ObjectVersionPageInner: React.FC<{
  objectVersion: ObjectVersionSchema;
}> = ({objectVersion}) => {
  useObjectViewEvent(objectVersion);

  const {useRootObjectVersions, useRefsData} = useWFHooks();
  const entityName = objectVersion.entity;
  const projectName = objectVersion.project;
  const objectName = objectVersion.objectId;
  const objectVersionIndex = objectVersion.versionIndex;
  const {refExtra, createdAtMs} = objectVersion;
  const objectVersions = useRootObjectVersions({
    entity: entityName,
    project: projectName,
    filter: {
      objectIds: [objectName],
    },
    metadataOnly: true,
    includeStorageSize: true,
  });
  const objectVersionCount = (objectVersions.result ?? []).length;
  const baseObjectClass = useMemo(() => {
    const s = objectVersion.baseObjectClass;
    return KNOWN_BASE_OBJECT_CLASSES.includes(s as KnownBaseObjectClassType)
      ? (s as KnownBaseObjectClassType)
      : null;
  }, [objectVersion.baseObjectClass]);
  const refUri = objectVersionKeyToRefUri(objectVersion);

  const producingCallStats = useCallsStats({
    entity: entityName,
    project: projectName,
    filter: {
      outputObjectVersionRefs: [refUri],
    },
  });
  const consumingCallStats = useCallsStats({
    entity: entityName,
    project: projectName,
    filter: {
      inputObjectVersionRefs: [refUri],
    },
  });

  const shouldShowProducingSection = useMemo(() => {
    return (
      producingCallStats.loading || (producingCallStats.result?.count ?? 0) > 0
    );
  }, [producingCallStats]);
  const shouldShowConsumingSection = useMemo(() => {
    return (
      consumingCallStats.loading || (consumingCallStats.result?.count ?? 0) > 0
    );
  }, [consumingCallStats]);
  const showCallsTab = useMemo(() => {
    return shouldShowProducingSection || shouldShowConsumingSection;
  }, [shouldShowProducingSection, shouldShowConsumingSection]);

  const data = useRefsData({refUris: [refUri]});
  const viewerData = useMemo(() => {
    if (data.loading) {
      return {};
    }
    return data.result?.[0] ?? {};
  }, [data.loading, data.result]);

  const showDeleteButton = useShowDeleteButton(entityName);

  const viewerDataAsObject = useMemo(() => {
    const dataIsPrimitive =
      typeof viewerData !== 'object' ||
      viewerData === null ||
      Array.isArray(viewerData);
    if (dataIsPrimitive) {
      // _result is a special key that is automatically removed by the
      // ObjectViewerSection component.
      return {_result: viewerData};
    }
    return viewerData;
  }, [viewerData]);

  const isPrompt = baseObjectClass === 'Prompt' && refExtra == null;
  const isDataset = baseObjectClass === 'Dataset' && refExtra == null;
  const isEvaluation = baseObjectClass === 'Evaluation' && refExtra == null;
  const isScorer = baseObjectClass === 'Scorer' && refExtra == null;
  const evalHasCalls = (consumingCallStats.result?.count ?? 0) > 0;
  const evalHasCallsLoading = consumingCallStats.loading;

  const storageSizeResult = useObjectStorageSizeCalculation(
    objectVersions,
    objectVersionIndex
  );

  const {
    currentVersionSizeBytes,
    allVersionsSizeBytes,
    shouldShowAllVersions,
    isLoading,
  } = storageSizeResult;

  if (isEvaluation && evalHasCallsLoading) {
    return <CenteredAnimatedLoader />;
  }

  if (isDataset) {
    return (
      <DatasetEditProvider>
        <DatasetVersionPage
          objectVersion={objectVersion}
          showDeleteButton={showDeleteButton}
        />
      </DatasetEditProvider>
    );
  }

  if (isPrompt) {
    return (
      <PromptVersionPage
        objectVersion={objectVersion}
        showDeleteButton={showDeleteButton}
        storageSizeResult={storageSizeResult}
      />
    );
  }

  return (
    <SimplePageLayoutWithHeader
      title={
        <Tailwind>
          <div className="flex items-center gap-8">
            {baseObjectClass && (
              <ObjectIcon baseObjectClass={baseObjectClass} />
            )}
            {objectVersionText(objectName, objectVersionIndex)}
          </div>
        </Tailwind>
      }
      headerContent={
        <Tailwind>
          <div className="grid-cols-auto grid w-full grid-flow-col gap-[16px] text-[14px]">
            <div className="block">
              <p className="text-moon-500">Name</p>
              <div className="flex items-center">
                <ObjectVersionsLink
                  entity={entityName}
                  project={projectName}
                  filter={{objectName}}
                  versionCount={objectVersionCount}
                  neverPeek
                  variant="secondary">
                  <div className="group flex items-center font-semibold">
                    <span>{objectName}</span>
                    {objectVersions.loading ? (
                      <LoadingDots />
                    ) : (
                      <span className="ml-[4px]">
                        ({objectVersionCount} version
                        {objectVersionCount !== 1 ? 's' : ''})
                      </span>
                    )}
                    <Icon
                      name="forward-next"
                      width={16}
                      height={16}
                      className="ml-[2px] opacity-0 group-hover:opacity-100"
                    />
                  </div>
                </ObjectVersionsLink>
              </div>
            </div>
            <div className="block">
              <p className="text-moon-500">Last updated</p>
              <p>
                <Timestamp value={createdAtMs / 1000} format="relative" />
              </p>
            </div>
            {objectVersion.userId && (
              <div className="block">
                <p className="text-moon-500">Last updated by</p>
                <UserLink userId={objectVersion.userId} includeName />
              </div>
            )}
            <StorageSizeSection
              isLoading={isLoading}
              shouldShowAllVersions={shouldShowAllVersions}
              currentVersionBytes={currentVersionSizeBytes}
              allVersionsSizeBytes={allVersionsSizeBytes}
            />
            {isScorer && (
              <div className="block">
                <p className="text-moon-500">Scores</p>
                <CallsLink
                  entity={entityName}
                  project={projectName}
                  neverPeek
                  filter={{
                    traceRootsOnly: false,
                  }}
                  gridFilters={{
                    items: [
                      {
                        id: 0,
                        field: callQueryFieldForScorerOutput(objectName),
                        operator: '(any): isNotEmpty',
                      },
                      // This second clause makes it version-specific
                      {
                        id: 1,
                        field: callQueryFieldForScorerVersion(objectName),
                        operator: '(string): equals',
                        value: refUri,
                      },
                    ],
                  }}
                />
              </div>
            )}
            {refExtra && (
              <div className="block">
                <p className="text-moon-500">Subpath</p>
                <p>{refExtra}</p>
              </div>
            )}
            {showDeleteButton && (
              <div className="ml-auto">
                <DeleteObjectButtonWithModal objVersionSchema={objectVersion} />
              </div>
            )}
          </div>
        </Tailwind>
      }
      tabs={[
        ...(isEvaluation && evalHasCalls
          ? [
              {
                label: 'Leaderboard',
                content: (
                  <EvaluationLeaderboardTab
                    entity={entityName}
                    project={projectName}
                    evaluationObjectName={objectName}
                    evaluationObjectVersion={objectVersion.versionHash}
                  />
                ),
              },
            ]
          : []),
        {
          label: 'Values',
          content: (
            <ScrollableTabContent>
              <Box
                sx={{
                  flex: '0 0 auto',
                  height: '100%',
                }}>
                {data.loading ? (
                  <CenteredAnimatedLoader />
                ) : (
                  <WeaveCHTableSourceRefContext.Provider value={refUri}>
                    <CustomWeaveTypeProjectContext.Provider
                      value={{entity: entityName, project: projectName}}>
                      <ObjectViewerSection
                        title=""
                        data={viewerDataAsObject}
                        noHide
                        isExpanded
                      />
                    </CustomWeaveTypeProjectContext.Provider>
                  </WeaveCHTableSourceRefContext.Provider>
                )}
              </Box>
            </ScrollableTabContent>
          ),
        },
        {
          label: 'Use',
          content: (
            <ScrollableTabContent>
              <Tailwind>
                {baseObjectClass === 'SavedView' ? (
                  <TabUseSavedView name={objectName} uri={refUri} />
                ) : baseObjectClass === 'Prompt' ? (
                  <TabUsePrompt
                    name={objectName}
                    uri={refUri}
                    entityName={entityName}
                    projectName={projectName}
                    data={viewerDataAsObject}
                    versionIndex={objectVersionIndex}
                  />
                ) : baseObjectClass === 'Model' ? (
                  <TabUseModel
                    name={objectName}
                    uri={refUri}
                    entityName={entityName}
                    projectName={projectName}
                    versionIndex={objectVersionIndex}
                  />
                ) : baseObjectClass === 'AnnotationSpec' ? (
                  <TabUseAnnotationSpec
                    name={objectName}
                    uri={refUri}
                    projectName={projectName}
                    data={viewerDataAsObject}
                  />
                ) : (
                  <TabUseObject
                    name={objectName}
                    uri={refUri}
                    entityName={entityName}
                    projectName={projectName}
                    versionIndex={objectVersionIndex}
                  />
                )}
              </Tailwind>
            </ScrollableTabContent>
          ),
        },
        ...(showCallsTab
          ? [
              {
                label: 'Calls',
                content: (
                  <CallsTab
                    entity={entityName}
                    project={projectName}
                    refUri={refUri}
                    shouldShowProducingSection={shouldShowProducingSection}
                    shouldShowConsumingSection={shouldShowConsumingSection}
                  />
                ),
              },
            ]
          : []),
      ]}
    />
  );
};

const CallsTab: React.FC<{
  entity: string;
  project: string;
  refUri: string;
  shouldShowProducingSection: boolean;
  shouldShowConsumingSection: boolean;
}> = ({
  entity,
  project,
  refUri,
  shouldShowProducingSection,
  shouldShowConsumingSection,
}) => {
  const columns = useMemo(() => {
    return ['id', 'op_name', 'project_id'];
  }, []);

  const producingCalls = useCalls({
    entity,
    project,
    filter: {
      outputObjectVersionRefs: [refUri],
    },
    columns,
  });

  const consumingCalls = useCalls({
    entity,
    project,
    filter: {
      inputObjectVersionRefs: [refUri],
    },
    columns,
  });

  return (
    <Tailwind>
      {shouldShowProducingSection && (
        <CallsContentSection
          calls={producingCalls.result ?? []}
          callsLoading={producingCalls.loading}
          loadingLabel="Producing Calls"
          callType="producing"
          refUri={refUri}
        />
      )}
      {shouldShowConsumingSection && (
        <CallsContentSection
          calls={consumingCalls.result ?? []}
          callsLoading={consumingCalls.loading}
          loadingLabel="Consuming Calls"
          callType="consuming"
          refUri={refUri}
        />
      )}
    </Tailwind>
  );
};

const CallsContent: React.FC<{
  calls: CallSchema[];
  callType: 'producing' | 'consuming';
  refUri: string;
}> = ({calls, callType, refUri}) => {
  const isProducing = callType === 'producing';

  return (
    <div className="ml-8">
      {isProducing ? (
        <ObjectVersionProducingCallsItem
          producingCalls={calls}
          refUri={refUri}
        />
      ) : (
        <ObjectVersionConsumingCallsItem
          consumingCalls={calls}
          refUri={refUri}
        />
      )}
    </div>
  );
};

const CallsContentSection: React.FC<{
  calls: CallSchema[];
  callsLoading: boolean;
  loadingLabel: string;
  callType: 'producing' | 'consuming';
  refUri: string;
}> = ({calls, callsLoading, loadingLabel, callType, refUri}) => {
  // Early return if no data to show
  if (!callsLoading && (calls.length ?? 0) === 0) {
    return null;
  }

  let tableData;

  if (callsLoading) {
    tableData = {[loadingLabel]: <LoadingDots />};
  } else {
    const count = calls.length;
    const callLabel =
      callType === 'producing' ? 'Producing Call' : 'Consuming Call';
    const pluralizedLabel = maybePluralizeWord(count, callLabel);
    tableData = {
      [pluralizedLabel]: (
        <CallsContent calls={calls} callType={callType} refUri={refUri} />
      ),
    };
  }

  return (
    <Box sx={{p: 2}}>
      <SimpleKeyValueTable data={tableData} keyColumnWidth={200} />
    </Box>
  );
};

const ObjectVersionProducingCallsItem: React.FC<{
  producingCalls: CallSchema[];
  refUri: string;
}> = props => {
  if (props.producingCalls.length === 1) {
    const call = props.producingCalls[0];
    const {opVersionRef, spanName} = call;
    if (opVersionRef == null) {
      return <>{spanName}</>;
    }
    return (
      <CallLink
        entityName={call.entity}
        projectName={call.project}
        opName={spanName}
        callId={call.callId}
        variant="secondary"
      />
    );
  }
  return (
    <GroupedCalls
      calls={props.producingCalls}
      partialFilter={{
        outputObjectVersionRefs: [props.refUri],
      }}
    />
  );
};
const ObjectVersionConsumingCallsItem: React.FC<{
  consumingCalls: CallSchema[];
  refUri: string;
}> = props => {
  if (props.consumingCalls.length === 1) {
    const call = props.consumingCalls[0];
    const {opVersionRef, spanName} = call;
    if (opVersionRef == null) {
      return <>{spanName}</>;
    }
    return (
      <CallLink
        entityName={call.entity}
        projectName={call.project}
        opName={spanName}
        callId={call.callId}
        variant="secondary"
      />
    );
  }
  return (
    <GroupedCalls
      calls={props.consumingCalls}
      partialFilter={{
        inputObjectVersionRefs: [props.refUri],
      }}
    />
  );
};

export const GroupedCalls: React.FC<{
  calls: CallSchema[];
  partialFilter?: WFHighLevelCallFilter;
}> = ({calls, partialFilter}) => {
  const callGroups = useMemo(() => {
    const groups: {
      [key: string]: {
        opVersionRef: string;
        callCount: number;
      };
    } = {};
    calls.forEach(call => {
      const {opVersionRef} = call;
      if (opVersionRef == null) {
        return;
      }
      if (groups[opVersionRef] == null) {
        groups[opVersionRef] = {
          opVersionRef,
          callCount: 0,
        };
      }
      groups[opVersionRef].callCount++;
    });
    return groups;
  }, [calls]);

  if (calls.length === 0) {
    return <div>-</div>;
  } else if (Object.keys(callGroups).length === 1) {
    const key = Object.keys(callGroups)[0];
    const val = callGroups[key];
    return <OpVersionCallsLink val={val} partialFilter={partialFilter} />;
  }
  return (
    <ul className="ml-8 list-none p-0">
      {Object.entries(callGroups).map(([key, val], ndx) => {
        return (
          <li key={key}>
            <OpVersionCallsLink val={val} partialFilter={partialFilter} />
          </li>
        );
      })}
    </ul>
  );
};

const OpVersionCallsLink: React.FC<{
  val: {
    opVersionRef: string;
    callCount: number;
  };
  partialFilter?: WFHighLevelCallFilter;
}> = ({val, partialFilter}) => {
  const opRef = refUriToOpVersionKey(val.opVersionRef);
  if (opRef == null) {
    return null;
  }
  const objRef = {
    entityName: opRef.entity,
    projectName: opRef.project,
    artifactName: opRef.opId,
    artifactVersion: opRef.versionHash,
    weaveKind: 'op' as const,
    scheme: 'weave' as const,
  };
  return (
    <div className="flex items-center gap-2">
      <SmallRef objRef={objRef as ObjectRef} />
      <div className="w-full text-right">
        <CallsLink
          entity={objRef.entityName}
          project={objRef.projectName}
          callCount={val.callCount}
          filter={{
            opVersionRefs: [val.opVersionRef],
            ...(partialFilter ?? {}),
          }}
          neverPeek
          variant="secondary"
        />
      </div>
    </div>
  );
};
